const STRUCTURED_TURN_FIELD =
  /"(answer|justification|excerpts_and_references)"\s*:/i;
const STRUCTURED_CODE_FENCE = /^```json(?:\s|$)/i;

// Citation selections are private model output, not displayable references.
// Wait for the server's validated quotation/source objects before showing cards.
export const isDisplayableReference = (value: any): boolean =>
  value !== null && typeof value === "object" &&
  typeof (value.text_excerpt ?? value.citedText) === "string" &&
  Boolean((value.text_excerpt ?? value.citedText).trim()) &&
  typeof (value.source_name ?? value.reference) === "string" &&
  Boolean((value.source_name ?? value.reference).trim());

export class StreamingContentRevisionBuffer {
  private visibleText = "";
  private stagedText: string | null = null;

  get isStagingRevision(): boolean {
    return this.stagedText !== null;
  }

  append(chunk: string): { text: string; shouldPublish: boolean } {
    if (this.stagedText !== null) {
      this.stagedText += chunk;
      return { text: this.stagedText, shouldPublish: false };
    }

    this.visibleText += chunk;
    return { text: this.visibleText, shouldPublish: true };
  }

  reset(reason?: string): { text: string; shouldClearVisible: boolean } {
    if (reason === "citation_validation" || this.stagedText !== null) {
      this.stagedText = "";
      return { text: "", shouldClearVisible: false };
    }

    this.visibleText = "";
    return { text: "", shouldClearVisible: true };
  }

  commitRevision(): string | null {
    if (this.stagedText === null) return null;
    this.visibleText = this.stagedText;
    this.stagedText = null;
    return this.visibleText;
  }

  discardRevision(): boolean {
    const wasStaging = this.stagedText !== null;
    this.stagedText = null;
    return wasStaging;
  }

  clear(): void {
    this.visibleText = "";
    this.stagedText = null;
  }
}

export const isStructuredThreadTurn = (accumulatedContent: string): boolean => {
  const trimmed = accumulatedContent.trimStart();
  return (
    trimmed.startsWith("{") ||
    STRUCTURED_CODE_FENCE.test(trimmed) ||
    STRUCTURED_TURN_FIELD.test(accumulatedContent)
  );
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const decodePartialJsonString = (raw: string): string => {
  let decoded = "";

  for (let index = 0; index < raw.length; index += 1) {
    const character = raw[index];
    if (character !== "\\") {
      decoded += character;
      continue;
    }

    const escape = raw[index + 1];
    if (escape === undefined) break;
    index += 1;

    switch (escape) {
      case '"':
      case "\\":
      case "/":
        decoded += escape;
        break;
      case "b":
        decoded += "\b";
        break;
      case "f":
        decoded += "\f";
        break;
      case "n":
        decoded += "\n";
        break;
      case "r":
        decoded += "\r";
        break;
      case "t":
        decoded += "\t";
        break;
      case "u": {
        const hex = raw.slice(index + 1, index + 5);
        if (!/^[0-9a-fA-F]{4}$/.test(hex)) {
          // Do not flash a partial unicode escape while the next chunk arrives.
          return decoded;
        }
        decoded += String.fromCharCode(parseInt(hex, 16));
        index += 4;
        break;
      }
      default:
        // Keep malformed provider output readable while full validation occurs
        // on the backend.
        decoded += escape;
        break;
    }
  }

  return decoded;
};

/**
 * Read a JSON string field before the complete JSON object has arrived.
 *
 * A fetch/SSE chunk can end after any character, including immediately after
 * an escape. Returning the complete prefix lets React repaint on every model
 * delta without waiting for JSON.parse to accept the full response.
 */
export const extractStreamingJsonString = (
  accumulatedContent: string,
  fieldName: string
): string | undefined => {
  const marker = new RegExp(
    `"${escapeRegExp(fieldName)}"\\s*:\\s*"`,
    "i"
  );
  const match = marker.exec(accumulatedContent);
  if (!match) return undefined;

  let raw = "";
  let escaping = false;
  for (
    let index = match.index + match[0].length;
    index < accumulatedContent.length;
    index += 1
  ) {
    const character = accumulatedContent[index];
    if (escaping) {
      raw += `\\${character}`;
      escaping = false;
      continue;
    }
    if (character === "\\") {
      escaping = true;
      continue;
    }
    if (character === '"') break;
    raw += character;
  }

  return decodePartialJsonString(raw);
};

/**
 * Return every complete object currently available in a streamed JSON array.
 *
 * Brackets and braces inside quoted excerpts are deliberately ignored. The
 * previous inline parser counted those characters and could stop references
 * from appearing until the final persisted-message refresh.
 */
export const extractStreamingJsonArrayObjects = (
  accumulatedContent: string,
  fieldName: string
): unknown[] | undefined => {
  const marker = new RegExp(
    `"${escapeRegExp(fieldName)}"\\s*:\\s*\\[`,
    "i"
  );
  const match = marker.exec(accumulatedContent);
  if (!match) return undefined;

  const arrayStart =
    match.index + match[0].lastIndexOf("[");
  const objects: unknown[] = [];
  let arrayDepth = 0;
  let objectDepth = 0;
  let objectStart = -1;
  let inString = false;
  let escaping = false;

  for (
    let index = arrayStart;
    index < accumulatedContent.length;
    index += 1
  ) {
    const character = accumulatedContent[index];

    if (escaping) {
      escaping = false;
      continue;
    }
    if (inString && character === "\\") {
      escaping = true;
      continue;
    }
    if (character === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (character === "[") {
      arrayDepth += 1;
      continue;
    }
    if (character === "]") {
      arrayDepth -= 1;
      if (arrayDepth === 0) {
        try {
          const parsed = JSON.parse(
            accumulatedContent.slice(arrayStart, index + 1)
          );
          return Array.isArray(parsed) ? parsed : objects;
        } catch {
          return objects;
        }
      }
      continue;
    }

    if (arrayDepth !== 1) continue;
    if (character === "{") {
      if (objectDepth === 0) objectStart = index;
      objectDepth += 1;
      continue;
    }
    if (character === "}") {
      objectDepth -= 1;
      if (objectDepth === 0 && objectStart !== -1) {
        try {
          objects.push(
            JSON.parse(accumulatedContent.slice(objectStart, index + 1))
          );
        } catch {
          // The object is not complete JSON yet; a later chunk will retry it.
        }
        objectStart = -1;
      }
    }
  }

  return objects;
};
